// src/features/dashboard/hooks/Employee/useEmployeeLoggedHours.ts

import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  useDashboardDate,
} from '../../context/DashboardDateContext';

import {
  formatApiDate,
} from '../../utils/date.utils';

import {
  getEmployeeCode,
} from '../../../auth/utils/Login/authStorage';

import {
  getLoggedHoursKpi,
  getSessionLog,
  getWtdLogged,
  getThirtyDayLogged,
  getTimeSplit,
  getMeVsTeamLogged,
  getDailyLog,
  getHourlyLogged,
} from '../../services/Employee/employeeLoggedHours.service';

import {
  LoggedHoursKpiResponse,
  SessionLogResponse,
  WtdLoggedResponse,
  Logged30DayResponse,
  TimeSplitResponse,
  MeVsTeamLoggedResponse,
  DailyLogResponse,
  HourlyLoggedResponse,
} from '../../types/Employee/employeeLoggedHours.types';

export const useEmployeeLoggedHours = () => {
  const {
    fromDate,
  } = useDashboardDate();

  /* =========================================================================
     EMPLOYEE CODE
     ========================================================================= */

  const employeeCode =
    getEmployeeCode() || '';

  /* =========================================================================
     STATE
     ========================================================================= */

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    kpi,
    setKpi,
  ] = useState<
    LoggedHoursKpiResponse | null
  >(null);

  const [
    sessionLog,
    setSessionLog,
  ] = useState<
    SessionLogResponse[]
  >([]);

  const [
    wtdLogged,
    setWtdLogged,
  ] = useState<
    WtdLoggedResponse[]
  >([]);

  const [
    trend30Day,
    setTrend30Day,
  ] = useState<
    Logged30DayResponse | null
  >(null);

  const [
    timeSplit,
    setTimeSplit,
  ] = useState<
    TimeSplitResponse[]
  >([]);

  const [
    meVsTeam,
    setMeVsTeam,
  ] = useState<
    MeVsTeamLoggedResponse | null
  >(null);

  const [
    dailyLog,
    setDailyLog,
  ] = useState<
    DailyLogResponse | null
  >(null);

  const [
    hourlyLogged,
    setHourlyLogged,
  ] = useState<
    HourlyLoggedResponse | null
  >(null);

  /* =========================================================================
     FETCH DATA
     ========================================================================= */

  const fetchData = useCallback(
    async () => {
      setLoading(true);

      const date =
        formatApiDate(
          fromDate,
        );

      console.log(
        'Employee Code:',
        employeeCode,
      );

      console.log(
        'Date:',
        date,
      );

      /* =====================================================================
         KPI
         ===================================================================== */

      try {
        const response =
          await getLoggedHoursKpi({
            employeeCode,
            date,
            weeklyTargetHours: 45,
            dailyTargetHours: 9,
            overtimeThreshold: 1,
          });

        console.log(
          '✅ KPI',
          response,
        );

        setKpi(response);
      } catch (error) {
        console.error(
          '❌ KPI',
          error,
        );

        setKpi(null);
      }

      /* =====================================================================
         SESSION LOG
         ===================================================================== */

      try {
        const response =
          await getSessionLog({
            employeeCode,
            date,
          });

        console.log(
          '✅ Session',
          response,
        );

        setSessionLog(
          response ?? [],
        );
      } catch (error) {
        console.error(
          '❌ Session',
          error,
        );

        setSessionLog([]);
      }

      /* =====================================================================
         WTD LOGGED
         ===================================================================== */

      try {
        const response =
          await getWtdLogged({
            employeeCode,
            date,
            targetHours: 9,
          });

        console.log(
          '✅ WTD API Response:',
          response,
        );

        const formattedWtd:
          WtdLoggedResponse[] =
          Array.isArray(response)
            ? response.map(
                (item) => {
                  /*
                   * API:
                   *
                   * workDate:
                   * 2026-06-29T00:00:00
                   *
                   * dayLabel:
                   * Monday 26
                   *
                   * UI:
                   *
                   * Mon29
                   */

                  const workDate =
                    item.workDate
                      ? item.workDate.split(
                          'T',
                        )[0]
                      : '';

                  const datePart =
                    workDate
                      ? workDate.split(
                          '-',
                        )[2]
                      : '';

                  const dayName =
                    item.dayLabel
                      ? item.dayLabel.split(
                          ' ',
                        )[0]
                      : '';

                  const shortDay =
                    dayName.substring(
                      0,
                      3,
                    );

                  return {
                    ...item,
                    dayLabel:
                      `${shortDay}${datePart}`,
                  };
                },
              )
            : [];

        console.log(
          '✅ WTD Formatted:',
          formattedWtd,
        );

        setWtdLogged(
          formattedWtd,
        );
      } catch (error) {
        console.error(
          '❌ WTD',
          error,
        );

        setWtdLogged([]);
      }

      /* =====================================================================
         30 DAY TREND
         ===================================================================== */

      try {
        const response =
          await getThirtyDayLogged({
            employeeCode,
            date,
          });

        console.log(
          '✅ 30 Day',
          response,
        );

        setTrend30Day(
          response,
        );
      } catch (error) {
        console.error(
          '❌ 30 Day',
          error,
        );

        setTrend30Day(null);
      }

      /* =====================================================================
         TIME SPLIT
         ===================================================================== */

      try {
        const response =
          await getTimeSplit({
            employeeCode,
            date,
          });

        console.log(
          '✅ Time Split',
          response,
        );

        setTimeSplit(
          response ?? [],
        );
      } catch (error) {
        console.error(
          '❌ Time Split',
          error,
        );

        setTimeSplit([]);
      }

      /* =====================================================================
         ME VS TEAM
         ===================================================================== */

      try {
        const response =
          await getMeVsTeamLogged({
            employeeCode,
            currentDate: date,
          });

        console.log(
          '✅ Me Vs Team',
          response,
        );

        setMeVsTeam(
          response,
        );
      } catch (error) {
        console.error(
          '❌ Me Vs Team',
          error,
        );

        setMeVsTeam(null);
      }

      /* =====================================================================
         DAILY LOG
         ===================================================================== */

      try {
        const response =
          await getDailyLog({
            employeeCode,
            date,
            dailyTargetHours: 9,
            weeklyTargetHours: 45,
          });

        console.log(
          '✅ Daily',
          response,
        );

        setDailyLog(
          response,
        );
      } catch (error) {
        console.error(
          '❌ Daily',
          error,
        );

        setDailyLog(null);
      }

      /* =====================================================================
         HOURLY LOGGED
         ===================================================================== */

      try {
        const response =
          await getHourlyLogged({
            employeeCode,
            date,
          });

        console.log(
          '✅ Hourly',
          response,
        );

        setHourlyLogged(
          response,
        );
      } catch (error) {
        console.error(
          '❌ Hourly',
          error,
        );

        setHourlyLogged(null);
      }

      /* =====================================================================
         FINISH LOADING
         ===================================================================== */

      setLoading(false);
    },
    [
      employeeCode,
      fromDate,
    ],
  );

  /* =========================================================================
     EFFECT
     ========================================================================= */

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* =========================================================================
     RETURN
     ========================================================================= */

  return {
    loading,

    kpi,

    sessionLog,

    wtdLogged,

    trend30Day,

    timeSplit,

    meVsTeam,

    dailyLog,

    hourlyLogged,

    refresh:
      fetchData,
  };
};